import React, { useEffect, useState } from "react";
import { faComments } from "@fortawesome/free-regular-svg-icons";
import EmptyState from "../pages/EmptyState";
import ListSkeleton from "../Skeleton/ListSkeleton";
import { SkeletonBlock } from "../Skeleton/Skeleton";
import useDelayedFlag from "../Skeleton/useDelayedFlag";
import ChatListRow from "./ChatListRow";
import ChatThread from "./ChatThread";
import Composer from "./Composer";
import ThreadHeader from "./ThreadHeader";
import useChatList from "./useChatList";
import useChatThread from "./useChatThread";
import { markThreadSeen } from "../../utils/unreadBadges";
import { toDate } from "../../utils/timeFormat";

// Right panel for a selected thread. Split out so mounting it per selection
// gives useChatThread a clean slate (history refetch + rejoin) instead of having
// to reconcile a thread swap in place.
function ThreadPanel({ chat, me }) {
  const { messages, loading, sending, sendText, sendAttachment } = useChatThread({
    receiverId: chat?.friend_details?.id,
    chatGroupId: chat?.id,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ThreadHeader user={chat?.friend_details} />
      {loading ? (
        // Thread history: alternating bubble blocks, bottom-aligned like real
        // messages. The header and composer above/below stay usable.
        <div
          className="flex min-h-0 flex-1 flex-col justify-end gap-3 p-4"
          aria-busy="true"
          aria-label="Messages loading"
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`flex ${i % 2 ? "justify-end" : "justify-start"}`}
            >
              <SkeletonBlock
                rounded="rounded-2xl"
                height={i % 3 === 0 ? 52 : 36}
                width={i % 2 ? "42%" : "48%"}
              />
            </div>
          ))}
        </div>
      ) : (
        <ChatThread
          messages={messages}
          myId={me}
          // Bubble max width 60% of the panel on desktop.
          maxBubbleClass="max-w-[60%]"
          className="min-h-0 flex-1"
        />
      )}
      <Composer onSend={sendText} onAttach={sendAttachment} disabled={sending} />
    </div>
  );
}

// Desktop (≥1024) Chats: one card, list on the left, conversation on the right.
export default function ChatsDesktop() {
  const { threads, loading, me, isUnread, refreshUnread } = useChatList();
  const [selectedId, setSelectedId] = useState(null);

  // Nothing is preselected — the right panel opens on "No conversation
  // selected" — but once a thread is chosen it stays selected across reloads.
  useEffect(() => {
    if (selectedId != null && !threads.some((c) => c.id === selectedId)) {
      setSelectedId(null);
    }
  }, [threads, selectedId]);

  const selected = threads.find((c) => c.id === selectedId) || null;

  const openThread = (chat) => {
    // Watermark from the row's own last-message timestamp — see markThreadSeen.
    markThreadSeen(
      chat.id,
      toDate(chat?.last_message?.created_at)?.getTime() || Date.now()
    );
    setSelectedId(chat.id);
    refreshUnread();
  };

  const showListSkeleton = useDelayedFlag(loading);

  // Loading / whole-screen empty state stay inside the card, same as every other
  // list screen. "No chats at all" reuses the existing empty-state copy + glyph.
  if (loading || threads.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-8">
        {loading ? (
          // Row skeletons inside the card, in the real list rhythm.
          showListSkeleton ? (
            <ListSkeleton rows={6} avatarSize={44} columns={0} />
          ) : null
        ) : (
          <EmptyState
            icon={faComments}
            title="No messages yet"
            body={
              <>
                When someone sends you a DM,
                <br />
                it'll show up here.
              </>
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-14rem)] min-h-[28rem] overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {/* Left: the list. Rows carry their own hover/selected/unread states. */}
      <div className="w-[19rem] flex-shrink-0 overflow-y-auto border-r border-divider">
        {threads.map((chat, i) => (
          <React.Fragment key={chat.id}>
            {/* Dividers inset to the handle edge; none below the last row. */}
            {i > 0 && <div className="ml-[68px] border-t border-divider" />}
            <ChatListRow
              chat={chat}
              unread={isUnread(chat)}
              selected={chat.id === selectedId}
              onOpen={() => openThread(chat)}
            />
          </React.Fragment>
        ))}
      </div>

      {/* Right: the conversation. Remounts per thread (keyed) so history and the
          socket join are re-run for the newly selected person. */}
      {selected ? (
        <ThreadPanel key={selected.id} chat={selected} me={me} />
      ) : (
        // Same cluster as "No messages yet" — identical glyph, circle, headline
        // and body styling, spacing and width, since it comes from the same
        // component. `!pt-0 !pb-0` drops EmptyState's default asymmetric padding
        // so the cluster lands dead-centre in the panel rather than 64px high.
        <div className="flex flex-1 items-center justify-center">
          <EmptyState
            icon={faComments}
            title="No conversation selected"
            body="Choose a chat to start reading."
            className="!pt-0 !pb-0"
          />
        </div>
      )}
    </div>
  );
}
